import { z } from 'zod';

import { createSafeEnum } from './utils';

const conversationStatusEnum = z.enum([
  'Processing',
  'Done',
  'Error',
  'Cancel',
  'Fatal',
]);
export const conversationStatus = createSafeEnum(
  conversationStatusEnum,
  'Error'
);

const taskStatusEnum = z.enum(['OK', 'Error', 'No-Content', 'Cancel', 'Fatal']);
export const taskStatus = createSafeEnum(taskStatusEnum, 'No-Content');

const accessLevelEnum = z.enum(['public', 'private']);
export const accessLevel = createSafeEnum(accessLevelEnum, 'private');

const cancelStepContentEnum = z.enum(['REQUEST_CANCELLED']);
export const cancelStepContent = createSafeEnum(
  cancelStepContentEnum,
  'REQUEST_CANCELLED'
);

const errorStepContentEnum = z.enum([
  'MAX_ITERATIONS',
  'SERVICE_BUSY',
  'UNSPECIFIED',
]);
export const errorStepContent = createSafeEnum(
  errorStepContentEnum,
  'UNSPECIFIED'
);

const fatalStepContentEnum = z.enum(['CONTEXT_LIMIT', 'REFUSAL']);
export const fatalStepContent = createSafeEnum(
  fatalStepContentEnum,
  'CONTEXT_LIMIT'
);

const analysisModeEnum = z.enum(['Quick', 'Deep']);
export const analysisMode = createSafeEnum(analysisModeEnum, 'Quick');

const mentionTypeEnum = z.enum(['entity', 'theme', 'list', 'public-list']);
export const mentionType = createSafeEnum(mentionTypeEnum, 'entity');

export const fatalContentValues = fatalStepContentEnum.options;

export const allFailedContentValues = [
  ...cancelStepContentEnum.options,
  ...errorStepContentEnum.options,
  ...fatalStepContentEnum.options,
];

const answerContentKeys = ['emit_answer', 'final_answer'] as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const stringifyAnswerContent = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const tryParseJsonRecord = (value: string): Record<string, unknown> | null => {
  const trimmed = value.trim();

  if (!trimmed.startsWith('{')) {
    return null;
  }

  try {
    const parsed = JSON.parse(trimmed);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const getAnswerPayloadContent = (value: unknown): string | undefined => {
  const payload = typeof value === 'string' ? tryParseJsonRecord(value) : value;

  if (!isRecord(payload)) {
    return undefined;
  }

  for (const key of answerContentKeys) {
    const answer = payload[key];

    if (answer !== undefined && answer !== null && answer !== '') {
      return stringifyAnswerContent(answer);
    }
  }

  return undefined;
};

export const normalizeAnswerContent = (value: unknown): string =>
  getAnswerPayloadContent(value) ?? stringifyAnswerContent(value);

const answerContent = z.preprocess(
  value => getAnswerPayloadContent(value) ?? value,
  z.string()
);

export const step = z.object({
  id: z.string(),
  name: z.string(),
  title: z.string(),
  start_time: z.string(),
  duration_seconds: z.number(),
  next: z.string(),
  status: taskStatus,
  content_type: z.string(),
  content: answerContent,
  analysis_mode: analysisMode.nullish(),
});

export const attachment = z.object({
  file_id: z.string(),
  file_name: z.string(),
  size: z.number(),
  content_type: z.string(),
});

export const mentionSchema = z.object({
  type: mentionType,
  id: z.string(),
  name: z.string(),
  label: z.string(),
});

const conversationMessageResponseSchema = z.object({
  content: answerContent.optional(),
  status: taskStatus,
});

const postConversationSchema = z.object({
  conversation_id: z.string(),
  request_id: z.string(),
  name: z.string(),
});

const continueConversationSchema = z.object({ request_id: z.string() });

export const conversationMessagesSchema = z.object({
  states: z.array(step),
});

const conversationMessage = z.object({
  request_id: z.string(),
  start_time: z.string(),
  total_seconds: z.number().optional(),
  duration_seconds: z.number().optional(),
  input: z.string(),
  output: answerContent.optional(),
  first_state: step.optional(),
  last_state: step.optional(),
  analysis_mode: analysisMode.nullish(),
  attachments: z.array(attachment).nullish(),
  mentions: z.array(mentionSchema).nullish(),
});

const conversationInfo = z.object({
  name: z.string(),
  date: z.string(), // updated_date, it is when the last question was asked (or when the conversation was copied).
  id: z.string(),
  summary: z.string(),
  favourite: z.boolean(),
  access_level: accessLevel,
  created_date: z.string(),
  status: conversationStatus.nullish(),
  scheduled_query_id: z.string().nullish(),
});

const getConversationTasksSchema = z.object({
  ...conversationInfo.shape,
  tasks: z.array(conversationMessage),
});

const conversationSearchResult = z.object({
  name: z.string(),
  conversation_id: z.string(),
  request_id: z.string(),
  updated_at: z.string(),
  state: step,
});

const searchConversationSchema = z.object({
  results: z.array(conversationSearchResult),
});

const conversationSchema = z.object({
  ...conversationInfo.shape,
  public_copies: z.array(conversationInfo),
});

const getAllConversationsSchema = z.object({
  conversations: z.array(conversationSchema),
});

export const feedbackEnum = z.enum(['Good', 'Bad', 'Neutral']);
const postConversationFeedback = z.object({
  conversation_id: z.string(),
  request_id: z.string(),
  feedback: feedbackEnum,
});

const copyConversationSchema = z.object({
  conversation_id: z.string(),
  name: z.string(),
});

const settingsSchema = z.object({
  prompt: z.string(),
});

const languageSettingsSchema = z.object({
  locale: z.string(),
});

export type LanguageSetting = z.infer<typeof languageSettingsSchema>;

const webSearchSettingsSchema = z.object({
  sources: z.array(z.string()).nullish(),
});

export type AccessLevel = z.infer<typeof accessLevel>;
export type CancelStepContent = z.infer<typeof cancelStepContent>;
export type ErrorStepContent = z.infer<typeof errorStepContent>;
export type FatalStepContent = z.infer<typeof fatalStepContent>;
export type AllFailedContentValues = (typeof allFailedContentValues)[number];
export type Step = z.infer<typeof step>;
export type ConversationMessageResponseSchema = z.infer<
  typeof conversationMessageResponseSchema
>;
export type ConversationMessagesSchema = z.infer<
  typeof conversationMessagesSchema
>;
export type PostConversationResponse = z.infer<typeof postConversationSchema>;
export type ContinueConversationResponse = z.infer<
  typeof continueConversationSchema
>;
export type BasedConversation = z.infer<typeof conversationSchema>; // base
export type ConversationSearchResult = z.infer<typeof conversationSearchResult>;
export type SearchConversationResponse = z.infer<
  typeof searchConversationSchema
>;
export type GetConversationTasksSchema = z.infer<
  typeof getConversationTasksSchema
>;
export type PostConversationFeedback = z.infer<typeof postConversationFeedback>;
export type ConversationMessageFeedback = z.infer<typeof feedbackEnum>;
export type ProcessingStatus = z.infer<typeof taskStatus>;
export type ConversationStatus = z.infer<typeof conversationStatus>;
export type Task = z.infer<typeof conversationMessage>;
export type Attachment = z.infer<typeof attachment>;
export type Mention = z.infer<typeof mentionSchema>;
export type WebSearchSettings = z.infer<typeof webSearchSettingsSchema>;

const SETTINGS_PATH = 'conversation/v2/settings';

export const conversations = {
  getAllConversations: {
    path: 'conversation/v3',
    schema: getAllConversationsSchema,
  },
  conversationId: {
    path: (id: string) => `conversation/v2/${id}`,
  },
  postConversation: { path: 'conversation/v3', schema: postConversationSchema },
  postConversationKgInsights: {
    path: 'conversation/v6',
    schema: postConversationSchema,
  },
  continueConversation: {
    path: (id: string) => `conversation/v3/${id}`,
    schema: continueConversationSchema,
  },
  getConversationTask: {
    path: (id: string) => `conversation/v3/${id}`,
    schema: getConversationTasksSchema,
  },
  searchConversation: {
    path: 'conversation/v2/search',
    schema: searchConversationSchema,
  },
  getConversationMessage: {
    path: (conversationId: string, requestId: string) =>
      `conversation/v2/${conversationId}/tasks/${requestId}`,
    schema: conversationMessagesSchema,
  },
  feedback: {
    path: 'conversation/v2/feedback',
    schema: postConversationFeedback,
  },
  abortConversation: {
    path: (id: string) => `conversation/v2/${id}/tasks`,
  },
  changeAccessLevel: {
    path: (id: string) => `conversation/v2/${id}/copy`,
    schema: copyConversationSchema,
  },
  getSettings: {
    path: `${SETTINGS_PATH}/preferences.format`,
    schema: settingsSchema,
  },
  putSettings: {
    path: `${SETTINGS_PATH}/preferences.format`,
  },
  getLanguageSetting: {
    path: `${SETTINGS_PATH}/preferences.language`,
    schema: languageSettingsSchema,
  },
  putLanguageSetting: {
    path: `${SETTINGS_PATH}/preferences.language`,
  },
  deleteLanguageSetting: {
    path: `${SETTINGS_PATH}/preferences.language`,
  },
  getCustomSources: {
    path: `${SETTINGS_PATH}/tools.websearch`,
    schema: webSearchSettingsSchema,
  },
  updateCustomSources: {
    path: `${SETTINGS_PATH}/tools.websearch`,
    schema: webSearchSettingsSchema,
  },
  quickDeep: { path: 'quick-deep/v1/analyze' },
};
